import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { Box, CircularProgress } from '@material-ui/core';
import { Flags } from '@setvi/shared/enums';
import { ROUTES } from 'enumsV2';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import { ErrorPage } from '../error-page';

export const AuthorizedRoutes = () => {
  const { token, user } = useAppContext();
  const location = useLocation();

  if (user && !user?.EmailVerified)
    return (
      <Navigate to={`${ROUTES.VERIFY_EMAIL}?email=${user.Email}`} replace />
    );

  if (token) return <Outlet />;

  return (
    <Navigate
      to={ROUTES.LOGIN}
      replace
      state={
        location?.pathname || location?.search || location?.hash
          ? location
          : null
      }
    />
  );
};

export const ModuleAuthorizedRoutes = ({ module }: { module: string }) => {
  const { companyData } = useAppContext();

  if (companyData[module as keyof typeof companyData]) return <Outlet />;
  return <Navigate to={ROUTES.NOT_FOUND} replace />;
};

interface FeatureFlagRouteProps {
  flag: Flags | Flags[];
  children: React.ReactNode;
}

export const FeatureFlagRoute = ({ flag, children }: FeatureFlagRouteProps) => {
  const ldClient = useLDClient();
  const flags = useFlags();

  if (!ldClient) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        flex={1}
        minHeight={320}>
        <CircularProgress />
      </Box>
    );
  }

  const flagsToCheck = Array.isArray(flag) ? flag : [flag];
  const isEnabled = flagsToCheck.some(f => flags?.[f]);

  if (!isEnabled)
    return (
      <ErrorPage
        src="/images/error-page.svg"
        title="An Unexpected Error Occurred"
        description="We apologize for the interruption. Please return to the previous page and try again. If the issue persists, please contact our support team for assistance."
      />
    );

  return <>{children}</>;
};
